"use client";

// Die Knoepfe im Kopf des Versandprotokolls und der Kasten einer
// geplanten Nachricht.
import Link from "next/link";
import { useRouter } from "next/navigation";
import { CalendarClock, Copy, Download, MoreHorizontal, Pencil, Send, Undo2 } from "lucide-react";
import { Button, buttonVariants } from "@/components/ui/button";
import { DropdownItem, DropdownMenu } from "@/components/ui/dropdown-menu";
import { confirmDialog } from "@/components/ui/confirm";
import { toast } from "@/components/ui/toast";
import { formatDateTime } from "@/lib/utils";
import { Hinweis } from "./zone";

export function ProtokollAktionen({ id, csv }: { id: string; csv: boolean }) {
  return (
    <>
      <Link href={`/nachrichten/neu?kopie=${id}`} className={buttonVariants({ variant: "outline" })}>
        <Copy className="h-4 w-4" aria-hidden />
        Als neue Nachricht
      </Link>
      {csv && (
        <DropdownMenu
          trigger={
            <Button variant="ghost" size="icon" aria-label="Weitere Aktionen">
              <MoreHorizontal className="h-4 w-4" aria-hidden />
            </Button>
          }
        >
          <DropdownItem
            onClick={() => {
              // BACKEND: GET /api/nachrichten/<id>/protokoll.csv — Name, Adresse, Weg, Status, Fehler, Zeitpunkte
              toast.success("Protokoll wird als CSV heruntergeladen");
            }}
          >
            <Download className="h-4 w-4" aria-hidden />
            Protokoll als CSV
          </DropdownItem>
        </DropdownMenu>
      )}
    </>
  );
}

export function GeplantKasten({ id, zeitpunkt, empfaenger }: { id: string; zeitpunkt: string; empfaenger: number }) {
  const router = useRouter();

  async function jetzt() {
    const ok = await confirmDialog({ title: `Jetzt an ${empfaenger} Empfänger senden?`, description: "Statt zum geplanten Zeitpunkt geht die Nachricht sofort hinaus.", confirmLabel: "Jetzt senden" });
    if (!ok) return;
    // BACKEND: sendMailing(id) — sofort statt geplant
    toast.success("Die Nachricht geht hinaus");
    router.push("/nachrichten");
  }

  async function aufheben() {
    const ok = await confirmDialog({ title: "Planung aufheben?", description: "Die Nachricht bleibt als Entwurf erhalten und geht nicht hinaus.", confirmLabel: "Planung aufheben" });
    if (!ok) return;
    // BACKEND: unscheduleMailing(id) — Mailing zurueck auf DRAFT
    toast.success("Planung aufgehoben — die Nachricht ist wieder ein Entwurf.");
    router.push(`/nachrichten/${id}/bearbeiten`);
  }

  return (
    <Hinweis ton="info" icon={<CalendarClock className="text-accent-2 h-4 w-4" aria-hidden />}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <p className="min-w-0 flex-1">
          Geht am <strong className="font-medium tabular-nums">{formatDateTime(zeitpunkt)}</strong> an {empfaenger} Empfänger hinaus. Bis dahin lässt sie sich ändern.
        </p>
        <div className="flex flex-wrap gap-2">
          <Link href={`/nachrichten/${id}/bearbeiten`} className={buttonVariants({ variant: "outline", size: "sm" })}>
            <Pencil className="h-3.5 w-3.5" aria-hidden />
            Bearbeiten
          </Link>
          <Button variant="ghost" size="sm" onClick={aufheben}>
            <Undo2 className="h-3.5 w-3.5" aria-hidden />
            Planung aufheben
          </Button>
          <Button size="sm" onClick={jetzt}>
            <Send className="h-3.5 w-3.5" aria-hidden />
            Jetzt senden
          </Button>
        </div>
      </div>
    </Hinweis>
  );
}
